/**
 * ANZ CSV Parser (pure, browser-safe).
 *
 * ANZ exports statements as headerless CSV with three columns:
 *   1. Date       — DD/MM/YYYY (Australian)
 *   2. Amount     — quoted signed decimal: "-199.00" or "199.00"
 *   3. Description — free text, may contain commas inside the quoted
 *                    field but in practice ANZ doesn't quote it; the
 *                    third column is "rest of line after the second
 *                    comma" so we parse manually rather than via a
 *                    generic CSV lib.
 *
 * Why a hand-rolled parser instead of papaparse: the file isn't
 * RFC-4180 compliant (description can contain unquoted commas, e.g.
 * "EFTPOS THE ORIGINAL PANCAKE K\,GAWLER..."), and the only thing we
 * really need is "first two cols then rest". Hand-rolled is 30 lines
 * and bullet-proof for this shape.
 */

export interface ParsedAnzRow {
  /** ISO YYYY-MM-DD. */
  date: string;
  /** Signed AUD amount as a JS number. */
  amount: number;
  /** Description exactly as the bank gave it (whitespace trimmed at
   *  ends only — internal whitespace preserved because some merchants
   *  rely on the spacing for legibility). */
  description: string;
}

export interface ParseResult {
  rows: ParsedAnzRow[];
  errors: Array<{ line: number; text: string; reason: string }>;
}

/** Convert "22/05/2026" → "2026-05-22". Returns null if invalid. */
function parseAnzDate(s: string): string | null {
  const m = s.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const dd = m[1].padStart(2, '0');
  const mm = m[2].padStart(2, '0');
  const yyyy = m[3];
  // Basic range check — JS Date is lenient (it would accept 31/02/2026
  // and silently roll over) so we validate component-wise.
  const day = parseInt(dd, 10);
  const month = parseInt(mm, 10);
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  const iso = `${yyyy}-${mm}-${dd}`;
  if (new Date(`${iso}T12:00:00Z`).toISOString().slice(0, 10) !== iso) return null;
  return iso;
}

/** Strip surrounding quotes if present and parse to a number. Returns
 *  null on anything unparseable. */
function parseAnzAmount(s: string): number | null {
  let v = s.trim();
  if (v.startsWith('"') && v.endsWith('"')) v = v.slice(1, -1);
  // Strip thousands commas just in case (haven't seen them in samples
  // but it's cheap defence).
  v = v.replace(/,/g, '');
  if (!/^[+-]?(?:\d+)(?:\.\d{1,2})?$/.test(v)) return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return n;
}

/** Split a single CSV line into [date, amount, description]. ANZ files
 *  don't quote the description field even when it contains commas, so
 *  a naive split(',') breaks. We hand-roll: first comma terminates
 *  date, then we look for the *quoted* amount field, then everything
 *  after that quote+comma is the description. */
function splitAnzLine(line: string): { date: string; amount: string; description: string } | null {
  // Find first comma (end of date).
  const firstComma = line.indexOf(',');
  if (firstComma === -1) return null;
  const date = line.substring(0, firstComma);
  const rest = line.substring(firstComma + 1);

  // Amount is either quoted "..." or bare. ANZ always quotes in the
  // samples we have, but tolerate both.
  let amount: string;
  let descStart: number;
  if (rest.startsWith('"')) {
    const closingQuote = rest.indexOf('"', 1);
    if (closingQuote === -1) return null;
    amount = rest.substring(1, closingQuote);
    // Expect a comma after the closing quote.
    if (rest[closingQuote + 1] !== ',') return null;
    descStart = closingQuote + 2;
  } else {
    const secondComma = rest.indexOf(',');
    if (secondComma === -1) return null;
    amount = rest.substring(0, secondComma);
    descStart = secondComma + 1;
  }

  const description = rest.substring(descStart);
  return { date, amount, description };
}

/** Parse a full ANZ CSV file (as text). Tolerates blank lines and
 *  optional trailing newlines. Returns rows in file order. */
export function parseAnzCsv(text: string): ParseResult {
  const rows: ParsedAnzRow[] = [];
  const errors: Array<{ line: number; text: string; reason: string }> = [];

  // Split on \r\n or \n. Don't trust the OS line ending.
  const lines = text.split(/\r?\n/);

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const line = raw.trim();
    if (!line) continue; // blank line — silently skip

    const parts = splitAnzLine(line);
    if (!parts) {
      errors.push({ line: i + 1, text: raw, reason: 'Could not split into date/amount/description columns' });
      continue;
    }

    const date = parseAnzDate(parts.date);
    if (!date) {
      errors.push({ line: i + 1, text: raw, reason: `Invalid date '${parts.date}' (expected DD/MM/YYYY)` });
      continue;
    }

    const amount = parseAnzAmount(parts.amount);
    if (amount === null) {
      errors.push({ line: i + 1, text: raw, reason: `Invalid amount '${parts.amount}'` });
      continue;
    }

    // Description: trim outer whitespace + collapse runs of internal
    // spaces to single spaces. ANZ pads with lots of whitespace which
    // makes search/dedupe fragile.
    const description = parts.description.trim().replace(/\s+/g, ' ');

    rows.push({ date, amount, description });
  }

  return { rows, errors };
}

/** Compute SHA-256 of a string using the Web Crypto API. Returns the
 *  hex digest. Browser-only. */
export async function sha256Hex(s: string): Promise<string> {
  const enc = new TextEncoder();
  const buf = await crypto.subtle.digest('SHA-256', enc.encode(s));
  const bytes = new Uint8Array(buf);
  let hex = '';
  for (let i = 0; i < bytes.length; i++) {
    hex += bytes[i].toString(16).padStart(2, '0');
  }
  return hex;
}
